import React, { useContext, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  makeStyles,
  TextField,
  Typography,
  Tooltip,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import SearchIcon from '@material-ui/icons/Search';
import ForumIcon from '@material-ui/icons/Forum';

import { useHistory, useParams } from 'react-router-dom';
import { AuthContext } from '../../context/Auth/AuthContext';
import { useDate } from '../../hooks/useDate';
import ConfirmationModal from '../../components/ConfirmationModal';
import { markChatRead } from '../../services/internalChatService';
import { getInternalChatUnreadForUser, isInternalChatOwner } from '../../utils/internalChatSync';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
    borderRight: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2, 2),
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
    backgroundColor: theme.palette.background.paper,
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    letterSpacing: '-0.02em',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
  },
  headerIconBtn: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(1),
    borderRadius: 10,
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      color: theme.palette.primary.main,
    },
  },
  searchWrap: {
    padding: theme.spacing(1.5, 2, 1),
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 12,
      backgroundColor: theme.palette.type === 'dark' ? theme.palette.background.default : '#f8fafc',
      fontSize: '0.875rem',
    },
  },
  filters: {
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1.25, 2),
    flexWrap: 'wrap',
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e8edf2'}`,
  },
  filterChip: {
    height: 30,
    fontSize: '0.8125rem',
    fontWeight: 600,
    borderRadius: 10,
    textTransform: 'none',
  },
  listScroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(1.5, 1.5, 2),
    ...theme.scrollbarStyles,
  },
  cardsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  chatCard: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1.5),
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
    border: `1px solid ${theme.palette.type === 'dark' ? theme.palette.divider : '#e2e8f0'}`,
    borderRadius: 12,
    padding: theme.spacing(1.5, 1.5),
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
    transition: theme.transitions.create(['box-shadow', 'border-color', 'transform'], {
      duration: 180,
    }),
    outline: 'none',
    '&:hover': {
      boxShadow: '0 4px 14px rgba(15, 23, 42, 0.08)',
      borderColor: alpha(theme.palette.primary.main, 0.35),
    },
    '&:focus-visible': {
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.4)}`,
    },
  },
  chatCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.35)}, 0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}`,
    backgroundColor: alpha(theme.palette.primary.main, theme.palette.type === 'dark' ? 0.12 : 0.06),
  },
  cardLeadRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    flexShrink: 0,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      fontSize: 24,
    },
  },
  avatarUnread: {
    backgroundColor:
      theme.palette.type === 'dark' ? alpha(theme.palette.success.main, 0.22) : '#d1fae5',
    color: theme.palette.type === 'dark' ? theme.palette.success.light : '#047857',
    border: `1px solid ${
      theme.palette.type === 'dark' ? alpha(theme.palette.success.main, 0.4) : '#a7f3d0'
    }`,
  },
  avatarCount: {
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.02em',
  },
  cardBody: {
    flex: 1,
    minWidth: 0,
  },
  cardTopRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginBottom: 4,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: '0.9375rem',
    lineHeight: 1.25,
    color: theme.palette.text.primary,
  },
  cardTime: {
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    marginTop: 2,
  },
  preview: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    fontSize: '0.8125rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
    marginTop: 2,
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  tagDotRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  tagDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    boxShadow: '0 0 0 1px rgba(15,23,42,0.12)',
  },
  cardActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flexShrink: 0,
    marginLeft: theme.spacing(0.5),
  },
  actionBtn: {
    padding: 6,
    borderRadius: 8,
    color: theme.palette.text.secondary,
    '&:hover': {
      backgroundColor: alpha(theme.palette.text.primary, 0.06),
      color: theme.palette.primary.main,
    },
  },
  actionBtnDanger: {
    '&:hover': {
      backgroundColor: alpha(theme.palette.error.main, 0.08),
      color: theme.palette.error.main,
    },
  },
}));

export default function ChatList({
  chats,
  handleSelectChat,
  handleDeleteChat,
  handleEditChat,
  onNewChat,
  onChatReadMarked,
  pageInfo,
  loading,
}) {
  void pageInfo;
  void loading;
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const { datetimeToClient } = useDate();

  const [confirmationModal, setConfirmModalOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState({});
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const { id } = useParams();

  const unreadMessages = (c) => getInternalChatUnreadForUser(c, user.id);

  const filteredChats = useMemo(() => {
    let list = Array.isArray(chats) ? [...chats] : [];
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) =>
        `${c.title || ''} ${c.lastMessage || ''}`.toLowerCase().includes(q)
      );
    }
    if (filter === 'unread') {
      list = list.filter((c) => unreadMessages(c) > 0);
    }
    return list;
  }, [chats, search, filter, user.id]);

  const goToMessages = async (chat) => {
    const hadUnread = unreadMessages(chat) > 0;
    if (hadUnread) {
      try {
        await markChatRead(chat.id);
        if (typeof onChatReadMarked === 'function') onChatReadMarked(chat.id);
      } catch (err) {
        /* ignore */
      }
    }

    if (id !== chat.uuid) {
      history.push(`/chats/${chat.uuid}`);
      const chatToSelect =
        hadUnread && Array.isArray(chat.users)
          ? {
              ...chat,
              users: chat.users.map((u) =>
                String(u.userId) === String(user.id) ? { ...u, unreads: 0 } : u
              ),
            }
          : chat;
      handleSelectChat(chatToSelect);
    }
  };

  const handleDelete = () => {
    handleDeleteChat(selectedChat);
  };

  return (
    <>
      <ConfirmationModal
        title="Excluir conversa"
        open={confirmationModal}
        onClose={setConfirmModalOpen}
        onConfirm={handleDelete}
      >
        Esta ação não pode ser revertida. Confirmar exclusão?
      </ConfirmationModal>

      <div className={classes.root}>
        <div className={classes.header}>
          <Typography className={classes.headerTitle} component="h2" variant="subtitle1">
            Conversas
          </Typography>
          <div className={classes.headerActions}>
            <Tooltip title="Nova conversa">
              <IconButton
                size="small"
                className={classes.headerIconBtn}
                onClick={onNewChat}
                aria-label="Nova conversa"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Em breve">
              <span>
                <IconButton
                  size="small"
                  className={classes.headerIconBtn}
                  disabled
                  aria-label="Mais opções"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>

        <div className={classes.searchWrap}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar conversas…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ opacity: 0.55 }} fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'aria-label': 'Buscar conversas' }}
          />
        </div>

        <div className={classes.filters}>
          <Chip
            label="Todas"
            size="small"
            className={classes.filterChip}
            clickable
            color={filter === 'all' ? 'primary' : 'default'}
            variant={filter === 'all' ? 'default' : 'outlined'}
            onClick={() => setFilter('all')}
          />
          <Chip
            label="Não lidas"
            size="small"
            className={classes.filterChip}
            clickable
            color={filter === 'unread' ? 'primary' : 'default'}
            variant={filter === 'unread' ? 'default' : 'outlined'}
            onClick={() => setFilter('unread')}
          />
        </div>

        <div className={classes.listScroll}>
          <div className={classes.cardsStack}>
            {filteredChats.map((chat) => {
              const unreads = unreadMessages(chat);
              const selected = chat.uuid === id;
              return (
                <Box
                  key={chat.id}
                  component="button"
                  type="button"
                  className={`${classes.chatCard} ${selected ? classes.chatCardSelected : ''}`}
                  onClick={() => goToMessages(chat)}
                >
                  <Box className={classes.cardLeadRow}>
                    <Avatar
                      className={`${classes.avatar} ${unreads > 0 ? classes.avatarUnread : ''}`}
                      aria-hidden
                    >
                      {unreads > 0 ? (
                        <span className={classes.avatarCount}>
                          {unreads > 99 ? '99+' : unreads}
                        </span>
                      ) : (
                        <ForumIcon />
                      )}
                    </Avatar>
                  </Box>
                  <div className={classes.cardBody}>
                    <div className={classes.cardTopRow}>
                      <Typography className={classes.cardTitle} noWrap component="span">
                        {chat.title || `Chat ${chat.id}`}
                      </Typography>
                      <Typography className={classes.cardTime} component="span">
                        {datetimeToClient(chat.updatedAt)}
                      </Typography>
                    </div>
                    {chat.lastMessage ? (
                      <Typography className={classes.preview} component="span">
                        {chat.lastMessage.replace(/<[^>]+>/g, '')}
                      </Typography>
                    ) : null}
                    {Array.isArray(chat.tags) && chat.tags.length > 0 && (
                      <div className={classes.metaRow}>
                        <div className={classes.tagDotRow}>
                          {chat.tags.map((tag) => (
                            <Tooltip key={tag.id} title={tag.name} placement="top">
                              <span
                                className={classes.tagDot}
                                style={{ backgroundColor: tag.color || '#999' }}
                              />
                            </Tooltip>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {isInternalChatOwner(chat, user.id) && handleEditChat && (
                    <div
                      className={classes.cardActions}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      role="presentation"
                    >
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          className={classes.actionBtn}
                          aria-label="Editar conversa"
                          onClick={() => handleEditChat(chat)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          className={`${classes.actionBtn} ${classes.actionBtnDanger}`}
                          aria-label="Excluir conversa"
                          onClick={() => {
                            setSelectedChat(chat);
                            setConfirmModalOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </Box>
              );
            })}
          </div>
          {filteredChats.length === 0 && (
            <Box px={2} py={5} textAlign="center">
              <Typography variant="body2" color="textSecondary">
                {search || filter === 'unread'
                  ? 'Nenhuma conversa encontrada.'
                  : 'Nenhuma conversa ainda. Use o + para criar.'}
              </Typography>
            </Box>
          )}
        </div>
      </div>
    </>
  );
}
